/**
 * One-shot client-side data migrations. Tracked by meta keys so each runs at
 * most once per IndexedDB.
 */
import { db, type LocalCustomExercise, type LocalExerciseSettings } from './db'
import { PRESET_EXERCISES } from './exerciseRegistry'

const META_MIGRATION_BUILTINS = 'migration:builtins-to-customs-v1'

/** Map a built-in exerciseKey to the per-builtin exerciseSettings field names
 * that need to be copied over to the new customExercises row. */
const SETTINGS_FIELD_MAP: Record<string, { rest?: keyof LocalExerciseSettings; weight?: keyof LocalExerciseSettings; defaultDuration?: keyof LocalExerciseSettings }> = {
  plank:           { rest: 'plankRestDuration', defaultDuration: 'plankDuration' },
  dumbbell_curl:   { rest: 'curlRestDuration', weight: 'curlWeight' },
  dumbbell_press:  { rest: 'dumbbellPressRestDuration', weight: 'dumbbellPressWeight' },
  glute_bridge:    { rest: 'gluteBridgeRestDuration' },
  push_up:         { rest: 'pushUpRestDuration' },
  squat:           { rest: 'squatRestDuration' },
  stair_climbing:  {},
  cycling:         {},
}

const BUILT_IN_KEYS = Object.keys(SETTINGS_FIELD_MAP)

/**
 * For users who used the app before the templates-only refactor: walk their
 * userExercises rows, and for any built-in key that doesn't yet have a
 * customExercises row, synthesize one from PRESET_EXERCISES + their old
 * exerciseSettings configuration.
 *
 * Idempotent via a meta-key flag. Safe to call on every app start.
 */
export async function migrateBuiltinsToCustomsOnce(): Promise<{ migrated: number }> {
  const done = await db.meta.get(META_MIGRATION_BUILTINS)
  if (done?.value === true) return { migrated: 0 }

  const presetByKey = new Map(PRESET_EXERCISES.map(p => [p.exerciseKey, p]))
  const [allUserEx, allCustoms, allSettings] = await Promise.all([
    db.userExercises.toArray(),
    db.customExercises.toArray(),
    db.exerciseSettings.toArray(),
  ])
  const customsByKey = new Map(allCustoms.map(c => [c.exerciseKey, c]))
  const settingsByKey = new Map(allSettings.map(s => [s.exerciseKey, s]))
  const now = new Date().toISOString()

  const rowsToInsert: LocalCustomExercise[] = []
  for (const ue of allUserEx) {
    if (!BUILT_IN_KEYS.includes(ue.exerciseKey)) continue
    if (customsByKey.has(ue.exerciseKey)) continue
    const preset = presetByKey.get(ue.exerciseKey)
    if (!preset) continue
    const settingsRow = settingsByKey.get(ue.exerciseKey)
    const fieldMap = SETTINGS_FIELD_MAP[ue.exerciseKey]
    const restFromSettings = fieldMap.rest && settingsRow ? settingsRow[fieldMap.rest] as number | null : null
    const weightFromSettings = fieldMap.weight && settingsRow ? settingsRow[fieldMap.weight] as number | null : null
    const defaultDurationFromSettings = fieldMap.defaultDuration && settingsRow ? settingsRow[fieldMap.defaultDuration] as number | null : null

    rowsToInsert.push({
      exerciseKey: ue.exerciseKey,
      name: preset.name,
      icon: preset.icon,
      template: preset.template,
      exerciseType: preset.exerciseType,
      restDuration: restFromSettings ?? preset.restDuration ?? null,
      defaultDuration: defaultDurationFromSettings ?? preset.defaultDuration ?? null,
      weight: weightFromSettings ?? preset.weight ?? null,
      quickReps1: preset.quickReps?.[0] ?? null,
      quickReps2: preset.quickReps?.[1] ?? null,
      quickReps3: preset.quickReps?.[2] ?? null,
      sortOrder: ue.sortOrder,
      createdAt: now,
      syncStatus: 'pending',
      pendingDelete: false,
    })
  }

  if (rowsToInsert.length > 0) {
    await db.customExercises.bulkAdd(rowsToInsert)
  }
  await db.meta.put({ key: META_MIGRATION_BUILTINS, value: true })
  return { migrated: rowsToInsert.length }
}

// ============================================================================
// v2 fix: dumbbell_* / glute_bridge were initially classified as
// sets_with_rest (count-only); they should be manual_reps (count + reps + weight).
// Idempotent via a separate meta key.
// ============================================================================

const META_FIX_DUMBBELL_TEMPLATES = 'migration:fix-dumbbell-templates-v1'

const DUMBBELL_FIX_MAP: Record<string, { quickReps: [number, number, number] }> = {
  dumbbell_curl:  { quickReps: [10, 12, 15] },
  dumbbell_press: { quickReps: [8, 10, 12] },
  glute_bridge:   { quickReps: [12, 15, 20] },
}

// ============================================================================
// v3 fix: upgrade any remaining sets_with_rest custom exercises (user-created
// or stale presets) to manual_reps. sets_with_rest was a count-only template;
// manual_reps is its superset (count + reps + weight).
// ============================================================================

const META_DROP_SETS_WITH_REST = 'migration:drop-sets-with-rest-v1'

export async function dropSetsWithRestOnce(): Promise<{ upgraded: number }> {
  const done = await db.meta.get(META_DROP_SETS_WITH_REST)
  if (done?.value === true) return { upgraded: 0 }

  // The template is typed as a 4-template union now, so cast for the lookup.
  const rows = await db.customExercises.toArray()
  const victims = rows.filter(r => (r.template as string) === 'sets_with_rest')
  for (const r of victims) {
    await db.customExercises.update(r.exerciseKey, {
      template: 'manual_reps',
      quickReps1: r.quickReps1 ?? 10,
      quickReps2: r.quickReps2 ?? 15,
      quickReps3: r.quickReps3 ?? 20,
      syncStatus: 'pending',
    })
  }
  await db.meta.put({ key: META_DROP_SETS_WITH_REST, value: true })
  return { upgraded: victims.length }
}

export async function fixDumbbellTemplatesOnce(): Promise<{ fixed: number }> {
  const done = await db.meta.get(META_FIX_DUMBBELL_TEMPLATES)
  if (done?.value === true) return { fixed: 0 }

  let fixed = 0
  for (const [key, cfg] of Object.entries(DUMBBELL_FIX_MAP)) {
    const row = await db.customExercises.get(key)
    if (!row) continue
    // Only fix rows that are still in the old sets_with_rest classification.
    if ((row.template as string) !== 'sets_with_rest') continue
    await db.customExercises.update(key, {
      template: 'manual_reps',
      quickReps1: row.quickReps1 ?? cfg.quickReps[0],
      quickReps2: row.quickReps2 ?? cfg.quickReps[1],
      quickReps3: row.quickReps3 ?? cfg.quickReps[2],
      syncStatus: 'pending',
    })
    fixed++
  }
  await db.meta.put({ key: META_FIX_DUMBBELL_TEMPLATES, value: true })
  return { fixed }
}
